import * as React from 'react';
import 'dayjs/locale/de';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGrowSetup } from '../GrowSetupContext';
import { useAlert } from './useAlert';

import hydrokontroller_img from '../assets/hydrokontroller.png';
import klimakontroller_img from '../assets/klimakontroller.png';
import DeviceClaim from './grow_setup/DeviceClaim';

import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Modal,
  IconButton,
} from '@mui/material';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import AddLinkRoundedIcon from '@mui/icons-material/AddLinkRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import DevicesOtherRoundedIcon from '@mui/icons-material/DevicesOtherRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

export default function GrowSetupConfiguration(props) {
  const { grow_setup, setGrowSetup, device_type, setDeviceType } = useGrowSetup();
  const [name, setName] = useState('None');

  const [klimakontroller, setKlimakontroller] = useState(null);
  const [hydrokontroller, setHydrokontroller] = useState(null);
  


  const [openClaim, setOpenClaim] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [loading, setLoading] = useState(false);

  const showAlert = useAlert();
  const navigate = useNavigate();

  // fetch settings
  const fetchData = async () => {
    if (!grow_setup) return;
    setLoading(true);
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${props.token}`,
    };
    try {
      const [settingsRes, devicesRes] = await Promise.all([
        axios.post(`/get_grow_setup_settings/${grow_setup}`, {}, { headers }),
        axios.get('/api/v4/devices', { params: { grow_setup_id: grow_setup }, headers }),
      ]);
      setName(settingsRes.data?.name ?? 'Unbenannt');
      const devices = devicesRes.data?.devices ?? [];
      const klima = devices.find(d => d.device_type === 1) ?? null;
      const hydro = devices.find(d => d.device_type === 2) ?? null;
      setKlimakontroller(klima);
      setHydrokontroller(hydro);
      setDeviceType(settingsRes.data?.device_type ?? 0);
    } catch (e) {
      console.error(e);
      showAlert('Einstellungen konnten nicht geladen werden.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grow_setup]);

  // Save name on blur / explicit click
  const save_name = async () => {
    if (!grow_setup) return;
    setSavingName(true);
    try {
      const res = await axios.post(`/grow_setup_change_name/${grow_setup}`, { data: name }, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${props.token}`,
        },
      });
      if (res.data === 'Name already taken') {
        showAlert('Dieser Name wird bereits von Ihnen verwendet.', 'error');
        return;
      }
      showAlert('Name gespeichert.', 'success');
    } catch (e) {
      console.error(e);
      showAlert('Name konnte nicht gespeichert werden.', 'error');
    } finally {
      setSavingName(false);
    }
  };

  const remove_device = async (deviceObj, label) => {
    if (!deviceObj) return;
    const device_id = deviceObj.device_id;
    try {
      await axios.post('/remove_device', { device_id }, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${props.token}`,
        },
      });
      showAlert(`${label} entfernt.`, 'success');
      // Zustand lokal aktualisieren statt reload
      if (label.includes('Klima')) setKlimakontroller(null);
      if (label.includes('Hydro')) setHydrokontroller(null);
    } catch (e) {
      console.error(e);
      showAlert(`${label} konnte nicht entfernt werden.`, 'error');
    }
  };

  const delete_grow_setup = async () => {
    if (!grow_setup) return;
    try {
      const res = await axios.post('/delete_grow_setup', { data: grow_setup }, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${props.token}`,
        },
      });
      if (res.data === 'Name already taken') {
        showAlert('Dieser Name wird bereits von Ihnen verwendet.', 'error');
        return;
      }
      showAlert('Grow Setup gelöscht.', 'success');
      navigate('/grow_setup');
    } catch (e) {
      console.error(e);
      showAlert('Grow Setup konnte nicht gelöscht werden.', 'error');
    } finally {
      setDeleteOpen(false);
    }
  };

  const DeviceCard = ({
    title,
    img,
    attachedDevice,
    onAttach,
    onDetach,
  }) => {
    const isAttached = Boolean(attachedDevice);
    const attachedId = attachedDevice?.device_id;
    const attachedName = attachedDevice?.device_name;
    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <CardHeader
          avatar={<DevicesOtherRoundedIcon />}
          title={title}
          subheader={
            <Chip
              size="small"
              label={isAttached ? 'verbunden' : 'nicht verbunden'}
              color={isAttached ? 'success' : 'default'}
              variant={isAttached ? 'filled' : 'outlined'}
              sx={{ ml: -0.5 }}
            />
          }
        />
        <CardMedia
          component="img"
          image={img}
          alt={title}
          sx={{
            flexShrink: 0,
            height: 160,
            objectFit: 'contain',
            bgcolor: (t) => (isAttached ? 'transparent' : t.palette.action.hover),
            opacity: isAttached ? 1 : 0.5,
          }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          {isAttached ? (
            <>
              {attachedName && (
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {attachedName}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Geräte-ID: {attachedId}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Kein Gerät zugeordnet. Füge jetzt ein Gerät hinzu.
            </Typography>
          )}
        </CardContent>
        <Divider />
        <CardActions sx={{ p: 2, gap: 1 }}>
          <Tooltip title="Gerät hinzufügen / claimen">
            <span>
              <Button
                variant="contained"
                startIcon={<AddLinkRoundedIcon />}
                onClick={onAttach}
                disabled={isAttached}
              >
                Hinzufügen
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Zuordnung entfernen">
            <span>
              <Button
                color="error"
                variant="outlined"
                startIcon={<LinkOffRoundedIcon />}
                onClick={onDetach}
                disabled={!isAttached}
              >
                Entfernen
              </Button>
            </span>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: '1 1 auto' }}>
          Grow‑Setup konfigurieren
        </Typography>
        <Tooltip title="Änderungen am Namen speichern">
          <span>
            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={save_name}
              disabled={savingName}
            >
              Speichern
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Grow Setup löschen (nicht rückgängig)">
          <span>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverRoundedIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Löschen
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Name */}
      <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ minWidth: 140 }} color="text.secondary">
            Setup‑Name
          </Typography>
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={save_name}
            size="small"
            sx={{ minWidth: 280, flex: '1 1 320px' }}
          />
        </CardContent>
      </Card>

      {/* Geräte */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <DeviceCard
            title="Klima‑Kontroller"
            img={klimakontroller_img}
            attachedDevice={klimakontroller}
            onAttach={() => setOpenClaim(true)}
            onDetach={() => remove_device(klimakontroller, 'Klima‑Kontroller')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DeviceCard
            title="Hydro‑Kontroller"
            img={hydrokontroller_img}
            attachedDevice={hydrokontroller}
            onAttach={() => setOpenClaim(true)}
            onDetach={() => remove_device(hydrokontroller, 'Hydro‑Kontroller')}
          />
        </Grid>
      </Grid>

      {/* Claim Modal */}
      <Modal open={openClaim} onClose={() => setOpenClaim(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90vw', sm: 520 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 2,
          }}
        >
          <DeviceClaim grow_setup={grow_setup} />
        </Box>
      </Modal>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Grow Setup löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Dieses Grow Setup und die Zuordnungen der Geräte werden entfernt. Dieser Vorgang kann
            nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={delete_grow_setup} startIcon={<DeleteForeverRoundedIcon />}>
            Endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
